import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { rankCandidates, scoreColor, recommendComplement } from "../services/compatibility";
import { getArchetype } from "../data/archetypes";
import "../styles.css";

function getInitials(name) { return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(); }
function getRoleClass(role) {
  const r = (role || "").toLowerCase();
  if (r.includes("supt")) return "supt";
  if (r.includes("pm")) return "pm";
  if (r.includes("apm")) return "apm";
  if (r.includes("fe")) return "fe";
  return "other";
}

const ROLE_OPTIONS = ["Sr. Supt", "Supt", "Finish Supt", "Asst. Supt", "FE", "Sr. PM", "PM", "APM", "PX"].sort((a, b) => a.localeCompare(b));

export default function BestFitFinder() {
  const { people: allPeople, loading, error } = useData();
  const people = (allPeople || []).filter(p => p.IsActive !== false && p.Archetype)
    .sort((a, b) => (a.FullName || "").localeCompare(b.FullName || ""));

  const [anchorId, setAnchorId] = useState("");
  const [anchorRole, setAnchorRole] = useState("Sr. Supt");
  const [targetRole, setTargetRole] = useState("PM");

  const anchor = people.find(p => String(p.Id) === String(anchorId));

  const ranked = useMemo(() => {
    if (!anchor) return [];
    // Candidate pool = everyone whose company role matches the target role
    const pool = people.filter(p => p.CompanyRole === targetRole || targetRole === "Any");
    return rankCandidates(anchor, anchorRole, pool, targetRole);
  }, [anchor, anchorRole, targetRole, people]);

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading people…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Best-Fit Finder</div>
          <div className="page-subtitle">Lock an anchor, find who complements them best</div>
        </div>
      </div>

      {people.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="users" /></div>
          <div className="empty-text">No profiled, active people yet.<br />Add Culture Index profiles to use this tool.</div>
        </div>
      ) : (
      <>
      {/* Controls */}
      <div className="card mb-24">
        <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label className="form-label">Anchor Person</label>
            <select className="form-select" value={anchorId} onChange={e => setAnchorId(e.target.value)}>
              <option value="">Select a person...</option>
              {people.map(p => (
                <option key={p.Id} value={p.Id}>{p.FullName} — {p.Archetype}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "0 1 150px" }}>
            <label className="form-label">Anchor's Role</label>
            <select className="form-select" value={anchorRole} onChange={e => setAnchorRole(e.target.value)}>
              {ROLE_OPTIONS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ fontSize: "20px", color: "var(--text-muted)", paddingBottom: "8px" }}>
            <Icon name="arrow-right" />
          </div>
          <div style={{ flex: "0 1 150px" }}>
            <label className="form-label">Find Best</label>
            <select className="form-select" value={targetRole} onChange={e => setTargetRole(e.target.value)}>
              {ROLE_OPTIONS.map(r => <option key={r}>{r}</option>)}
              <option value="Any">Any role</option>
            </select>
          </div>
        </div>
      </div>

      {/* Anchor display */}
      {anchor && (
        <div className="finder-anchor mb-24">
          <div className="finder-anchor-av" style={{ background: "var(--navy)", color: "white" }}>{getInitials(anchor.FullName)}</div>
          <div>
            <div style={{ fontSize: "10px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "1.5px", fontWeight: 700 }}>Anchored on</div>
            <div style={{ fontSize: "18px", fontWeight: 800 }}>{anchor.FullName}</div>
            <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{anchorRole} · {anchor.Archetype}</div>
          </div>
          <div style={{ marginLeft: "auto", fontSize: "13px", color: "var(--text-secondary)" }}>
            Ranking <b style={{ color: "var(--text-primary)" }}>{ranked.length}</b> {targetRole === "Any" ? "people" : `${targetRole}s`}
          </div>
        </div>
      )}

      {/* Optimal-add recommendation */}
      {anchor && ranked.length > 0 && (() => {
        const rec = recommendComplement([anchor]);
        if (!rec || rec.satisfied) return null;
        return (
          <div className="finder-rec">
            <div className="finder-rec-icon"><Icon name="bulb" size={20} /></div>
            <div style={{ flex: 1 }}>
              <div className="finder-rec-title">Optimal addition: {rec.primaryNeed.label}</div>
              <div className="finder-rec-text">
                {anchor.FullName} pairs best with someone bringing <b>{rec.primaryNeed.note}</b>.
                Look for these archetypes:
                <span className="finder-rec-arch">
                  {rec.archetypes.map(a => {
                    const ar = getArchetype(a);
                    return <span key={a} className="finder-rec-pill"><Icon name={ar.icon} size={12} /> {a}</span>;
                  })}
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Results */}
      {!anchor ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="users" /></div>
          <div className="empty-text">Select an anchor person above to find their best-fit teammates.</div>
        </div>
      ) : ranked.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><Icon name="user-search" /></div>
          <div className="empty-text">No profiled {targetRole}s available to rank.<br />Add more profiles to expand the candidate pool.</div>
        </div>
      ) : (
        <div>
          <div className="section-title mb-16">Ranked {targetRole === "Any" ? "Candidates" : `${targetRole}s`} for {anchor.FullName}</div>
          {ranked.map((r, i) => {
            const col = scoreColor(r.score);
            const isBest = i === 0;
            return (
              <Link key={r.person.Id} to={`/people/${r.person.Id}`} className={`finder-card ${isBest ? "best" : ""}`}>
                {isBest && <span className="finder-best-tag">Best Match</span>}
                <div className="finder-rank" style={isBest ? { color: "var(--green-bar)" } : {}}>{i + 1}</div>
                <div className={`finder-av avatar ${getRoleClass(targetRole)}`}>{getInitials(r.person.FullName)}</div>
                <div className="finder-person">
                  <div className="finder-pname">
                    {r.person.FullName}
                    <span className="finder-badge-mini">
                      <Icon name={r.archetype.icon} /> {r.person.Archetype}
                    </span>
                  </div>
                  <div className="finder-reason">{r.summary}</div>
                </div>
                <div className="finder-score">
                  <div className="finder-score-num" style={{ color: col.text }}>{r.score}</div>
                  <div className="finder-score-bar"><div className="finder-score-fill" style={{ width: `${r.score}%`, background: col.bar }} /></div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
}
